import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, Star } from 'lucide-react';
import { formatMoney, isMoneyString, toCents } from '@tcrd/shared';
import { Button, Card, cn, Notice, PaymentStatusBadge, SectionTitle, SelectField, TextArea, TextField } from '@tcrd/ui';
import { useToast } from '../../components/toast';
import { api, errorMessage, newIdempotencyKey } from '../../lib/api';
import { openAuthenticatedFile } from '../../lib/download';
import type { RideDto } from '../../lib/types';
import type { PaymentDto } from '../common/payments-types';

type CustomerPayment = PaymentDto & { payTo?: { bankAccounts: Array<{ bank: string; accountType: string; number: string; holder: string; holderId?: string }> } };

/** Pago y calificación desde la vista del cliente (reporta, nunca confirma). */
export function CustomerPaymentCard({ ride }: { ride: RideDto }) {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ['payment', `/rides/${ride.id}/payment`], queryFn: () => api<CustomerPayment>(`/rides/${ride.id}/payment`), enabled: !!ride.payment });
  const [reporting, setReporting] = useState(false);
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<'CASH' | 'TRANSFER'>('TRANSFER');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const p = q.data;

  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: ['payment'] }), qc.invalidateQueries({ queryKey: ['ride', ride.id] })]);

  const report = async () => {
    if (!p || !isMoneyString(amount)) return toast('Monto inválido', 'danger');
    try {
      await api(`/payments/${p.id}/report`, { body: { amount, method, reference: reference || undefined, note: note || undefined }, idempotencyKey: newIdempotencyKey() });
      setReporting(false);
      await refresh();
      toast('Reporte enviado al conductor', 'success');
    } catch (e) {
      toast(errorMessage(e), 'danger');
    }
  };

  const review = async () => {
    try {
      await api(`/rides/${ride.id}/review`, { body: { rating, comment: comment || undefined } });
      await qc.invalidateQueries({ queryKey: ['ride', ride.id] });
      toast('¡Gracias por tu calificación!', 'success');
    } catch (e) {
      toast(errorMessage(e), 'danger');
    }
  };

  const pendingAdjustments = p?.transactions.filter((t) => t.type === 'ADJUSTMENT' && t.status === 'REPORTED') ?? [];
  const hasPendingReport = p?.transactions.some((t) => t.type === 'FARE' && t.status === 'REPORTED');

  return (
    <div className="flex flex-col gap-3">
      {p && (
        <Card className="p-5">
          <SectionTitle title="Pago" action={<PaymentStatusBadge status={p.status} />} />
          <dl className="text-sm grid grid-cols-2 gap-y-1">
            <dt className="text-[var(--text-muted)]">Tarifa acordada</dt><dd className="text-right tabular-nums">{formatMoney(p.totalDue)}</dd>
            <dt className="text-[var(--text-muted)]">Pagado (verificado)</dt><dd className="text-right tabular-nums">{formatMoney(p.farePaid)}</dd>
            {toCents(p.tipTotal) > 0n && (<><dt className="text-[var(--text-muted)]">Propina</dt><dd className="text-right tabular-nums">{formatMoney(p.tipTotal)}</dd></>)}
            <dt className="font-semibold">Saldo</dt><dd className="text-right font-semibold tabular-nums">{formatMoney(p.balance)}</dd>
          </dl>
          {hasPendingReport && <Notice>Reportaste un pago; está pendiente de que el conductor lo verifique.</Notice>}

          {pendingAdjustments.map((t) => (
            <div key={t.id} className="mt-3 rounded-2xl border-2 border-amber-400/70 p-3 text-sm">
              Cargo adicional de <strong>{formatMoney(t.amount)}</strong>: <span data-no-translate>{t.note}</span>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <Button size="sm" variant="secondary" onClick={() => api(`/payments/transactions/${t.id}/respond`, { body: { accept: false } }).then(refresh)}>Rechazar</Button>
                <Button size="sm" variant="accent" onClick={() => api(`/payments/transactions/${t.id}/respond`, { body: { accept: true } }).then(refresh)}>Aceptar</Button>
              </div>
            </div>
          ))}

          {p.payTo && p.payTo.bankAccounts.length > 0 && toCents(p.balance) > 0n && (
            <details className="mt-3 text-sm">
              <summary className="cursor-pointer font-medium">Cuentas para transferir</summary>
              <ul className="mt-2 flex flex-col gap-2">
                {p.payTo.bankAccounts.map((b, i) => (
                  <li key={i} className="rounded-xl bg-[var(--surface-2)] p-3">
                    <p className="font-semibold">{b.bank} · {b.accountType}</p>
                    <p className="font-mono">{b.number}</p>
                    <p className="text-[var(--text-muted)]">{b.holder}{b.holderId ? ` · ${b.holderId}` : ''}</p>
                  </li>
                ))}
              </ul>
            </details>
          )}

          {reporting ? (
            <div className="mt-4 flex flex-col gap-3">
              <div className="grid grid-cols-2 gap-3">
                <TextField label="Monto (RD$)" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={p.balance} />
                <SelectField label="Método" value={method} onChange={(e) => setMethod(e.target.value as 'CASH' | 'TRANSFER')}>
                  <option value="TRANSFER">Transferencia</option>
                  <option value="CASH">Efectivo</option>
                </SelectField>
              </div>
              {method === 'TRANSFER' && <TextField label="Referencia" value={reference} onChange={(e) => setReference(e.target.value)} />}
              <TextField label="Comentario o discrepancia" value={note} onChange={(e) => setNote(e.target.value)} />
              <div className="grid grid-cols-2 gap-2">
                <Button variant="ghost" onClick={() => setReporting(false)}>Cancelar</Button>
                <Button onClick={report}>Enviar reporte</Button>
              </div>
              <p className="text-xs text-[var(--text-muted)]">El conductor confirmará la recepción del dinero.</p>
            </div>
          ) : (
            <div className="mt-4 flex flex-wrap gap-2">
              {toCents(p.balance) > 0n && p.status !== 'VOID' && <Button size="sm" variant="secondary" onClick={() => (setAmount(p.balance), setReporting(true))}>Reportar pago</Button>}
              <Button size="sm" variant="secondary" icon={<FileText className="size-4" />} onClick={() => openAuthenticatedFile(`/payments/${p.id}/receipt.pdf`, `comprobante-${ride.code}.pdf`).catch((e) => toast(errorMessage(e), 'danger'))}>Comprobante</Button>
            </div>
          )}
        </Card>
      )}

      {ride.status === 'COMPLETED' && !ride.review && (
        <Card className="p-5">
          <SectionTitle title="Califica tu viaje" />
          <div className="flex gap-1" role="radiogroup" aria-label="Calificación">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} role="radio" aria-checked={rating === n} aria-label={`${n} estrellas`} onClick={() => setRating(n)} className="tap flex items-center justify-center">
                <Star className={cn('size-8', n <= rating ? 'fill-amber-400 text-amber-400' : 'text-[var(--border)]')} />
              </button>
            ))}
          </div>
          {rating > 0 && (
            <div className="mt-3 flex flex-col gap-3">
              <TextArea label="Comentario (opcional)" value={comment} onChange={(e) => setComment(e.target.value)} maxLength={600} />
              <Button onClick={review}>Enviar calificación</Button>
            </div>
          )}
        </Card>
      )}
      {ride.review && (
        <Card className="p-4 text-sm flex items-center gap-2">
          <Star className="size-5 fill-amber-400 text-amber-400" aria-hidden /> Calificaste este viaje con {ride.review.rating} de 5.
        </Card>
      )}
    </div>
  );
}
